import {
    HStack,
    VStack,
    Text,
    Tooltip,
    Box,
    Icon,
    FormControl,
    Input,
    InputGroup,
    IconButton,
    Button,
    BoxProps,
    StackProps,
    Switch,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Radio,
    chakra,
    Checkbox,
} from '@chakra-ui/react';
import { HiOutlineTrash, HiPlus } from 'react-icons/hi';
import { BsThreeDotsVertical } from 'react-icons/bs';
import React from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import TextareaAutosize from 'react-textarea-autosize';
import { MdDragIndicator } from 'react-icons/md';
import { IconType } from 'react-icons';
import { EnumMemberItemContainerProps, EnumMemberItemViewProps } from '../../member';
import { ReportingViewBaseConfig } from '../../../../base';
import { FieldListViewProps } from './fieldListProps';
import { FieldItemContainerProps, FieldItemViewProps } from '../item';

const ChakraTextareaAutosize = chakra(TextareaAutosize, {
    baseStyle: {
        // any default Chakra styles you want here
        // e.g. fontSize: "md", etc.
    },
});

export function createFieldListView(
    config: ReportingViewBaseConfig & {
        Container: {
            Member: {
                Item: React.FC<EnumMemberItemContainerProps>;
            };
            Field: {
                Item: React.FC<FieldItemContainerProps>;
            };
        };
    }
): React.FC<FieldListViewProps> {
    const {
        UI: {
            Application: { createErrorBoundary },
            View,
        },
        Container: {
            Member: { Item: MemberItemContainer },
            Field: { Item: FieldItemContainer },
        },
    } = config;

    const styles = {
        gutter: {
            width: 10,
            spacing: 4,
        },
    } satisfies {
        gutter: {
            width: BoxProps['w'] & number;
            spacing: StackProps['spacing'] & number;
        };
    };

    const FieldOptionsMenu = View.createMenu({});

    const FieldItem: React.FC<FieldItemViewProps> = (props) => {
        return (
            <HStack w="full" role="group" spacing={styles.gutter.spacing} align="end">
                <FormControl
                    as={VStack}
                    spacing={4}
                    borderWidth={1}
                    borderColor="whiteAlpha.300"
                    p={6}
                    borderRadius="lg"
                    _hover={{ borderColor: 'whiteAlpha.400' }}
                >
                    <HStack justify="space-between" w="full" align="start">
                        <ChakraTextareaAutosize
                            {...props.field.title.input}
                            ref={props.field.title.ref}
                            fontSize="2xl"
                            fontWeight="bold"
                            color="whiteAlpha.900"
                            placeholder="Field name"
                            bg="none"
                            border="none"
                            outline="none"
                            resize="none"
                            minH="auto"
                            flex="1"
                            _focus={{
                                outline: 'none',
                                boxShadow: 'none',
                            }}
                        />
                        <HStack spacing={3} align="center">
                            {!props.required && (
                                <Tooltip
                                    closeOnClick={true}
                                    placement="top"
                                    openDelay={300}
                                    px={2}
                                    py={1}
                                    bg="gray.50"
                                    color="gray.900"
                                    borderRadius="md"
                                    shouldWrapChildren={true}
                                    label="This field is optional for respondents"
                                >
                                    <Text
                                        color="whiteAlpha.500"
                                        fontSize="xs"
                                        fontWeight="bold"
                                        textTransform="uppercase"
                                        userSelect="none"
                                    >
                                        Optional
                                    </Text>
                                </Tooltip>
                            )}
                            <HStack spacing={2}>
                                <Menu>
                                    <Tooltip
                                        closeOnClick={true}
                                        placement="top"
                                        openDelay={300}
                                        px={2}
                                        py={1}
                                        bg="gray.50"
                                        color="gray.900"
                                        borderRadius="md"
                                        shouldWrapChildren={true}
                                        label="Field type"
                                    >
                                        <MenuButton
                                            {...props.field.type.button}
                                            onFocus={(event) => event.preventDefault()}
                                            as={Button}
                                            size="sm"
                                            variant="outline"
                                            borderColor="whiteAlpha.300"
                                            color="whiteAlpha.800"
                                            bg="none"
                                            _hover={{
                                                bg: 'whiteAlpha.100',
                                            }}
                                            _expanded={{
                                                bg: 'whiteAlpha.200',
                                            }}
                                            rightIcon={
                                                <ChevronDownIcon
                                                    fontSize="sm"
                                                    color="whiteAlpha.500"
                                                />
                                            }
                                            leftIcon={
                                                <Icon
                                                    as={props.field.type.current.Icon}
                                                    fontSize="sm"
                                                />
                                            }
                                        >
                                            {props.field.type.current.label}
                                        </MenuButton>
                                    </Tooltip>
                                    <MenuList
                                        minW="16rem"
                                        bg="black"
                                        p={0}
                                        overflow="hidden"
                                    >
                                        <VStack
                                            align="start"
                                            w="full"
                                            bg="whiteAlpha.200"
                                            p={1}
                                            spacing={0}
                                        >
                                            {props.field.type.options.map((item) => {
                                                const isSelected =
                                                    item.value === props.type;
                                                return (
                                                    <MenuItem
                                                        key={item.id}
                                                        borderRadius="md"
                                                        aria-selected={isSelected}
                                                        py={1}
                                                        px={2}
                                                        onClick={item.onClick}
                                                        fontSize="sm"
                                                        fontWeight="medium"
                                                        color="whiteAlpha.800"
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        _hover={{
                                                            bg: 'whiteAlpha.50',
                                                        }}
                                                    >
                                                        <HStack spacing={3}>
                                                            <Box
                                                                p={1}
                                                                borderRadius="md"
                                                                bg="whiteAlpha.50"
                                                                borderWidth={1}
                                                                borderColor="whiteAlpha.100"
                                                            >
                                                                <Icon
                                                                    fontSize="lg"
                                                                    color="whiteAlpha.600"
                                                                    as={item.Icon}
                                                                />
                                                            </Box>
                                                            <Text>{item.label}</Text>
                                                        </HStack>
                                                        {isSelected && (
                                                            <Radio
                                                                isChecked={isSelected}
                                                                colorScheme="blue"
                                                                size="sm"
                                                                readOnly={true}
                                                            />
                                                        )}
                                                    </MenuItem>
                                                );
                                            })}
                                        </VStack>
                                    </MenuList>
                                </Menu>
                                <FieldOptionsMenu {...props.menu}>
                                    <IconButton
                                        onFocus={(event) => event.preventDefault()}
                                        bg="none"
                                        borderColor="whiteAlpha.300"
                                        borderWidth={1}
                                        size="sm"
                                        aria-label={
                                            props.menu.header?.label || 'Field options'
                                        }
                                        icon={<BsThreeDotsVertical fontSize="xl" />}
                                        _hover={{ bg: 'whiteAlpha.100' }}
                                        _expanded={{
                                            bg: 'whiteAlpha.200',
                                        }}
                                    />
                                </FieldOptionsMenu>
                            </HStack>
                        </HStack>
                    </HStack>

                    {props.field.description && (
                        <InputGroup>
                            <Input
                                {...props.field.description.input}
                                placeholder="Field description"
                                borderColor="whiteAlpha.300"
                                color="whiteAlpha.800"
                                _placeholder={{
                                    color: 'whiteAlpha.500',
                                }}
                                _hover={{
                                    borderColor: 'whiteAlpha.400',
                                }}
                            />
                        </InputGroup>
                    )}

                    {/* Show enum options if field type is enum */}
                    {props.type === 'enum' && props.field.options && (
                        <VStack spacing={3} w="full" align="start" overflow="visible">
                            {props.field.type.constraints && (
                                <Text
                                    fontSize="md"
                                    color="whiteAlpha.500"
                                    fontWeight="medium"
                                    userSelect="none"
                                >
                                    ({props.field.type.constraints.label})
                                </Text>
                            )}
                            <VStack spacing={0} w="full" align="start" overflow="visible">
                                {props.field.options.items.map((option) => (
                                    <MemberItemContainer
                                        key={option.id}
                                        as={EnumOptionView}
                                        {...option}
                                    />
                                ))}
                            </VStack>
                            <Button
                                size="sm"
                                bg="none"
                                color="whiteAlpha.500"
                                leftIcon={<Icon as={HiPlus} />}
                                onClick={props.field.options.onAdd}
                                _hover={{
                                    bg: 'whiteAlpha.100',
                                }}
                            >
                                Add option
                            </Button>
                        </VStack>
                    )}

                    {props.type === 'boolean' && (
                        <Box
                            w={4}
                            h={4}
                            bg="whiteAlpha.50"
                            borderRadius="sm"
                            borderWidth={2}
                            borderColor="whiteAlpha.300"
                        />
                    )}

                    {(props.type === 'text' || props.type === 'integer') && (
                        <InputGroup>
                            <Input
                                placeholder="Respondent's answer"
                                borderColor="whiteAlpha.100"
                                userSelect="none"
                                cursor="default"
                                isReadOnly={true}
                                _placeholder={{
                                    color: 'whiteAlpha.400',
                                    _hover: {
                                        color: 'whiteAlpha.400',
                                    },
                                }}
                                _hover={{
                                    color: 'whiteAlpha.400',
                                }}
                                _focus={{
                                    outline: 'none',
                                    boxShadow: 'none',
                                }}
                                _focusVisible={{
                                    outline: 'none',
                                    boxShadow: 'none',
                                }}
                            />
                        </InputGroup>
                    )}
                </FormControl>
            </HStack>
        );
    };

    const EnumOptionView: React.FC<EnumMemberItemViewProps> = (props) => {
        return (
            <Box position="relative" w="full">
                {/* Insertion line indicator - above */}
                {props.isDragOver && props.insertPosition === 'above' && (
                    <Box
                        position="absolute"
                        top="-1px"
                        left={0}
                        right={0}
                        h={0.5}
                        bg="whiteAlpha.400"
                        borderRadius="full"
                        zIndex={1}
                    />
                )}

                <HStack
                    w="calc(100% + 24px)"
                    ml={-3}
                    justify="space-between"
                    py={1}
                    px={3}
                    _hover={{ bg: 'whiteAlpha.50' }}
                    borderRadius="md"
                    role="group"
                    transition="background-color 0.2s ease"
                    onMouseEnter={props.hover.onEnter}
                    onMouseLeave={props.hover.onLeave}
                    onDragOver={props.drag.onOver}
                    onDrop={props.drag.onDrop}
                    onDragLeave={props.drag.onLeave}
                >
                    <HStack flex={1} spacing={3}>
                        <Box
                            w={4}
                            h={4}
                            bg="whiteAlpha.50"
                            borderRadius="sm"
                            borderWidth={2}
                            borderColor="whiteAlpha.300"
                        />
                        <Input
                            {...props.input}
                            borderRadius="sm"
                            placeholder="Option label"
                            variant="unstyled"
                            color="whiteAlpha.800"
                            fontWeight="semibold"
                            _placeholder={{
                                color: 'whiteAlpha.500',
                            }}
                        />
                    </HStack>
                    <HStack
                        opacity={props.isHovered ? 1 : 0}
                        spacing={1}
                        transition="opacity 0.2s ease"
                    >
                        <Box
                            as="div"
                            p={2}
                            cursor={props.isDragging ? 'grabbing' : 'grab'}
                            borderRadius="md"
                            _hover={{ bg: 'whiteAlpha.100' }}
                            aria-label="Drag option"
                            draggable
                            onDragStart={props.drag.onStart}
                            onDragEnd={props.drag.onEnd}
                        >
                            <Icon
                                as={MdDragIndicator}
                                fontSize="lg"
                                color="whiteAlpha.500"
                            />
                        </Box>
                        <IconButton
                            aria-label="Remove option"
                            size="sm"
                            bg="none"
                            isDisabled={!props.canRemove}
                            icon={
                                <Icon
                                    as={HiOutlineTrash}
                                    color={
                                        props.canRemove
                                            ? 'whiteAlpha.500'
                                            : 'whiteAlpha.300'
                                    }
                                    fontSize="lg"
                                />
                            }
                            onClick={props.onRemove}
                            _hover={props.canRemove ? { bg: 'whiteAlpha.100' } : {}}
                            _disabled={{
                                opacity: 0.6,
                                cursor: 'not-allowed',
                            }}
                        />
                    </HStack>
                </HStack>

                {/* Insertion line indicator - below */}
                {props.isDragOver && props.insertPosition === 'below' && (
                    <Box
                        position="absolute"
                        bottom="-1px"
                        left={0}
                        right={0}
                        h={0.5}
                        bg="whiteAlpha.400"
                        borderRadius="full"
                        zIndex={1}
                    />
                )}
            </Box>
        );
    };

    // Add button component for adding fields at the end
    const AddMenu: React.FC<
        React.PropsWithChildren<{
            options: Array<{
                id: string;
                label: string;
                Icon: IconType;
                onClick: () => void;
            }>;
            placement?: 'top' | 'right' | 'left' | 'bottom';
        }>
    > = ({ options, placement = 'top', children }) => {
        return (
            <Menu placement={placement}>
                {children}
                <MenuList minW="12rem" bg="black" p={0} overflow="hidden">
                    <VStack align="start" w="full" bg="whiteAlpha.200" p={1} spacing={0}>
                        {options.map((option) => (
                            <MenuItem
                                key={option.id}
                                borderRadius="md"
                                py={1}
                                px={2}
                                onClick={option.onClick}
                                fontSize="sm"
                                fontWeight="medium"
                                color="whiteAlpha.800"
                                _hover={{
                                    bg: 'whiteAlpha.50',
                                }}
                            >
                                <HStack spacing={2}>
                                    <Box
                                        p={1}
                                        borderRadius="md"
                                        bg="whiteAlpha.50"
                                        borderWidth={1}
                                        borderColor="whiteAlpha.100"
                                    >
                                        <Icon
                                            fontSize="lg"
                                            color="whiteAlpha.600"
                                            as={option.Icon}
                                        />
                                    </Box>
                                    <Text>{option.label}</Text>
                                </HStack>
                            </MenuItem>
                        ))}
                    </VStack>
                </MenuList>
            </Menu>
        );
    };

    // Field separator component with hover state for insert button
    const FieldSeparator: React.FC<{
        index: number;
        addOptions: Array<{
            id: string;
            label: string;
            Icon: IconType;
            onClick: () => void;
        }>;
        onInsert: (optionId: string, insertIndex: number) => void;
    }> = ({ index, addOptions, onInsert }) => {
        const [isHovered, setIsHovered] = React.useState(false);
        const [isMenuOpen, setIsMenuOpen] = React.useState(false);

        // Button should be visible when hovered or menu is open
        const isVisible = isHovered || isMenuOpen;

        return (
            <Box position="relative" h={0} w="full">
                {/* Invisible hover area that extends above and below */}
                <Box
                    position="absolute"
                    top="-12"
                    bottom="-12"
                    left={0}
                    right={0}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    pointerEvents="auto"
                    zIndex={0}
                >
                    {/* Button positioned within the hover area to prevent flickering */}
                    <HStack
                        position="absolute"
                        top="9" // 12 - 3 = 9 (offset from top of hover area)
                        left={-8}
                        right={0}
                        justify="start"
                        opacity={isVisible ? 1 : 0}
                        transition="opacity 0.2s ease-in-out"
                        pointerEvents={isVisible ? 'auto' : 'none'}
                    >
                        <Menu
                            placement="top"
                            onOpen={() => setIsMenuOpen(true)}
                            onClose={() => setIsMenuOpen(false)}
                        >
                            <Tooltip
                                closeOnClick={true}
                                placement="left"
                                openDelay={300}
                                px={2}
                                py={1}
                                bg="gray.50"
                                color="gray.900"
                                borderRadius="md"
                                shouldWrapChildren={true}
                                label="Insert field"
                            >
                                <MenuButton
                                    as={IconButton}
                                    onFocus={(event) => event.preventDefault()}
                                    aria-label="Insert field"
                                    h={6}
                                    w={6}
                                    minW="initial"
                                    minH="initial"
                                    borderRadius="full"
                                    borderWidth={1}
                                    borderColor="whiteAlpha.200"
                                    color="whiteAlpha.400"
                                    bg="whiteAlpha.100"
                                    icon={<Icon as={HiPlus} fontSize="md" />}
                                    _hover={{
                                        bg: 'whiteAlpha.200',
                                        borderColor: 'whiteAlpha.300',
                                    }}
                                    _expanded={{
                                        bg: 'whiteAlpha.300',
                                    }}
                                    transition="all 0.2s ease"
                                />
                            </Tooltip>
                            <MenuList minW="12rem" bg="black" p={0} overflow="hidden">
                                <VStack
                                    align="start"
                                    w="full"
                                    bg="whiteAlpha.200"
                                    p={1}
                                    spacing={0}
                                >
                                    {addOptions.map((option) => (
                                        <MenuItem
                                            key={option.id}
                                            borderRadius="md"
                                            py={1}
                                            px={2}
                                            onClick={() => onInsert(option.id, index)}
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color="whiteAlpha.800"
                                            _hover={{
                                                bg: 'whiteAlpha.50',
                                            }}
                                        >
                                            <HStack spacing={2}>
                                                <Box
                                                    p={1}
                                                    borderRadius="md"
                                                    bg="whiteAlpha.50"
                                                    borderWidth={1}
                                                    borderColor="whiteAlpha.100"
                                                >
                                                    <Icon
                                                        fontSize="lg"
                                                        color="whiteAlpha.600"
                                                        as={option.Icon}
                                                    />
                                                </Box>
                                                <Text>{option.label}</Text>
                                            </HStack>
                                        </MenuItem>
                                    ))}
                                </VStack>
                            </MenuList>
                        </Menu>
                    </HStack>
                </Box>
            </Box>
        );
    };

    return (props) => {
        if (props.items.length === 0) {
            return (
                <HStack justify="center" w="full">
                    <VStack
                        w="full"
                        align="center"
                        spacing={4}
                        bg="whiteAlpha.50"
                        p={6}
                        borderRadius="lg"
                    >
                        <Text color="whiteAlpha.500" fontSize="md" fontWeight="medium">
                            Add question
                        </Text>
                        <AddMenu options={props.add.options}>
                            <Tooltip
                                closeOnClick={true}
                                placement="top"
                                openDelay={300}
                                px={2}
                                py={1}
                                bg="gray.50"
                                color="gray.900"
                                borderRadius="md"
                                shouldWrapChildren={true}
                                label="Add field"
                            >
                                <MenuButton
                                    as={IconButton}
                                    onFocus={(event) => event.preventDefault()}
                                    aria-label="Add field"
                                    borderWidth={1}
                                    borderColor="whiteAlpha.300"
                                    borderRadius="full"
                                    color="whiteAlpha.500"
                                    bg="none"
                                    icon={<Icon as={HiPlus} fontSize="lg" />}
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                    _focus={{
                                        outline: 'none',
                                        boxShadow: 'none',
                                    }}
                                    _focusVisible={{
                                        outline: 'none',
                                        boxShadow: 'none',
                                    }}
                                    _expanded={{
                                        bg: 'whiteAlpha.100',
                                    }}
                                />
                            </Tooltip>
                        </AddMenu>
                    </VStack>
                </HStack>
            );
        }
        return (
            <VStack align="start" w="full" spacing={8}>
                <VStack w="full" align="start" spacing={4}>
                    {props.items.flatMap((field, index) => {
                        const elements = [];

                        // Add insert button before field (except for first field)
                        if (index > 0) {
                            elements.push(
                                <FieldSeparator
                                    key={`insert-${index}`}
                                    index={index}
                                    addOptions={props.add.options}
                                    onInsert={props.onInsert}
                                />
                            );
                        }

                        // Add the field
                        elements.push(
                            <Box key={field.id} w="full">
                                <FieldItemContainer as={FieldItem} {...field} />
                            </Box>
                        );

                        return elements;
                    })}
                </VStack>
                <HStack w="full" justify="center">
                    <AddMenu options={props.add.options}>
                        <Tooltip
                            closeOnClick={true}
                            placement="top"
                            openDelay={300}
                            px={2}
                            py={1}
                            bg="gray.50"
                            color="gray.900"
                            borderRadius="md"
                            shouldWrapChildren={true}
                            label="Add field"
                        >
                            <MenuButton
                                as={IconButton}
                                onFocus={(event) => event.preventDefault()}
                                aria-label="Add field"
                                borderRadius="full"
                                borderWidth={1}
                                borderColor="whiteAlpha.300"
                                color="whiteAlpha.500"
                                bg="none"
                                icon={<Icon as={HiPlus} fontSize="lg" />}
                                _hover={{
                                    bg: 'whiteAlpha.50',
                                }}
                                _expanded={{
                                    bg: 'whiteAlpha.100',
                                }}
                            />
                        </Tooltip>
                    </AddMenu>
                </HStack>
            </VStack>
        );
    };
}
